﻿import {translation} from "@2d-front-monorepo/core";
import {MaterialIcon, Message, PopOver} from "@2d-front-monorepo/design-library";
import {
	Attachments,
	AttachmentStatus,
	AttachmentStatusComponent,
	attachmentTypeLabel,
	createAttachmentHref,
	EntityDocument,
	IntegraHolderData,
	residencyToCompactString,
	Tariffs
} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, useMemo} from "react";
import {Link} from "react-router";
import {API_ENDPOINT} from "../../api-endpoint";
import {DocumentsFilterComponent} from "../../components/Filters/DocumentsFilterComponent";
import {FilteredItemsList} from "../../components/Filters/FilteredItemsList";
import {FilteredItemsProvider} from "../../components/Filters/FilteredItemsProvider";
import {formatDateTime} from "../../functions";
import {usePermits} from "../../hooks/usePermits";
import {formatRoute, PrivateRoutes} from "../../routes";

type DocumentControlProps = {
	document: EntityDocument;
	holderData: IntegraHolderData;
}

const filesPrefix = API_ENDPOINT.fileDownload;

function getViewHref(document: EntityDocument): string {
	const {entityType, entity} = document;

	switch (entityType) {
		case "holder":
			return formatRoute(PrivateRoutes.holderView, entity.id);

		case "permit":
			return formatRoute(PrivateRoutes.permitsView, entity.id);

		case "driver":
			return formatRoute(PrivateRoutes.driverView, entity.id);

		case "residency":
			return formatRoute(PrivateRoutes.residenciesView, entity.id);

		case "vehicle":
			return formatRoute(PrivateRoutes.vehiclesView, entity.id);
	}
}

function EntityControl({document, holderData}: DocumentControlProps): JSX.Element {
	const {entityType, entity} = document;

	switch (entityType) {
		case "holder":
			return <>{entity.email}</>;

		case "permit":
			return <>{Tariffs.get(holderData, entity.tariffId)}</>;

		case "driver":
			return <>{entity.fullName}</>;

		case "residency":
			return <>{residencyToCompactString(entity)}</>;

		case "vehicle":
			return <>{entity.plate}</>;
	}
}

function DocumentControl({document, holderData}: DocumentControlProps): JSX.Element {
	const {entityType, attachment} = document;

	const attachmentDownloadHref = createAttachmentHref(attachment, filesPrefix);
	const editable = attachment.status === AttachmentStatus.Rejected || attachment.status === AttachmentStatus.Expired;

	return (
		<tr>
			<td>{translation(attachmentTypeLabel(attachment.type))}</td>
			<td>{translation("entity__type--" + entityType)}</td>
			<td className="text"><EntityControl document={document} holderData={holderData}/></td>
			<td className="date">{attachment.expires == null ? "" : formatDateTime(attachment.expires)}</td>
			<td><AttachmentStatusComponent attachment={attachment} small={false}/></td>
			<td className="actions">
				<div>
					<PopOver>
						<Link to={getViewHref(document)}>
							<MaterialIcon icon="visibility"/>
							{translation(PermitTranslations.view)}
						</Link>
						<a href={attachmentDownloadHref} target="_blank" download>
							<MaterialIcon icon="download"/>
							{translation(PermitTranslations.download)}
						</a>
						{editable &&
                            <Link to={formatRoute(PrivateRoutes.documentsUpdate, attachment.id)}>
                                <MaterialIcon icon="upload_file"/>
								{translation(PermitTranslations.upload)}
                            </Link>
						}
					</PopOver>
				</div>
			</td>
		</tr>);
}


export default function Documents(): JSX.Element {
	const {permits, holderData} = usePermits();
	const documents = useMemo(() => Attachments.list(permits), [permits]);

	if (documents.length == 0) {
		return (<Message title={{text: "No documents found"}} type="information"/>);
	}

	return (
		<>
			<h1>{translation(PermitTranslations.documents_page__title)}</h1>
			<hr/>
			<FilteredItemsProvider items={documents}>
				<DocumentsFilterComponent/>
				<div role="region" tabIndex={0} className="table--card">
					<table>
						<thead>
						<tr>
							<th>{translation(PermitTranslations.document_type)}</th>
							<th>{translation(PermitTranslations.entity_type)}</th>
							<th className="text">{translation(PermitTranslations.entity_information)}</th>
							<th className="date">{translation(PermitTranslations.expiration_date)}</th>
							<th>{translation(PermitTranslations.document_status)}</th>
							<th className="actions"></th>
						</tr>
						</thead>
						<tbody>
						<FilteredItemsList
							keyExtractor={(document: EntityDocument) => document.attachment.id}
							renderItem={(document: EntityDocument) => (
								<DocumentControl document={document} holderData={holderData}/>
							)}/>
						</tbody>
					</table>
				</div>
			</FilteredItemsProvider>
		</>
	);
}
